import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import colors from "styles/colors";
import { TextHeader } from "components/Texts";
import ToggleSwitch from "components/ToggleSwitch";
import FacebookAdsCRMConversionSheets from "components/facebook/CRMConversionSheets";
import FacebookAdsCRMConversionCrm from "components/facebook/CRMConversionCrm";
import Button from "components/Buttons";
import Toast from "components/Toast";
import Modal from "components/Modal";
import Loading from "components/gads/Loading";
import facebookActions from "modules/facebook/actions";
import facebookSelectors from "modules/facebook/selectors";
import mainSelectors from "modules/main/selectors";
import googleSelectors from "modules/google/selectors";

const CRMConversion = ({ selectedMenu }) => {
  const [t] = useTranslation();
  const rmpToken = sessionStorage.getItem("rmp_token");
  const dispatch = useDispatch();
  const { REACT_APP_GOOGLE_ADS_SETUP_MANUAL_URL, REACT_APP_ENABLE_GOOGLE, REACT_APP_ENABLE_GOOGLE_WHITELIST } =
    process.env;
  const [currentTab, setCurrentTab] = useState("r-crm");
  const [loading, setLoading] = useState(true);
  const [modalActiveConversionDisplay, setModalActiveConversionDisplay] = useState("hide");
  const [modalInactiveConversionDisplay, setModalInactiveConversionDisplay] = useState("hide");
  const [conversionStatusLoading, setConversionStatusLoading] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showFailedToast, setShowFailedToast] = useState(false);
  const facebookSettingsResult = useSelector((state) => facebookSelectors.getFacebookSettingsResult(state));
  const detailsPixelId = useSelector((state) => facebookSelectors.getDetailsPixelIdResult(state));
  const conversionLoading = useSelector((state) => facebookSelectors.setCRMConversionsLoadingResult(state));
  const RcrmAppAvailableResult = useSelector((state) => mainSelectors.checkAppAvailableResult(state));
  const businessInfoResult = useSelector((state) => googleSelectors.getBusinessInfoResult(state));
  const menu = "facebook_ads_crm_conversion";

  const RenderContent = useMemo(() => {
    if (currentTab === "r-crm") {
      sessionStorage.setItem("selectedTab", "r-crm");
      return <FacebookAdsCRMConversionCrm />;
    } else if (
      currentTab === "g-sheets" &&
      (REACT_APP_ENABLE_GOOGLE == "TRUE" ||
        (businessInfoResult?.data?.business_id &&
          REACT_APP_ENABLE_GOOGLE_WHITELIST.search(businessInfoResult?.data?.business_id) >= 0))
    ) {
      sessionStorage.setItem("selectedTab", "g-sheets");
      return <FacebookAdsCRMConversionSheets />;
    }
  }, [currentTab]);

  const changeCurrentTab = (tab) => {
    setCurrentTab(tab);
    if (tab === "g-sheets") {
      dispatch(
        facebookActions.getGoogleSheetsCRMConversionsLastSend({
          token: rmpToken,
        }),
      );
    }
  };

  useEffect(() => {
    getFacebookSettings();
    if (
      RcrmAppAvailableResult &&
      RcrmAppAvailableResult.result === 0 &&
      RcrmAppAvailableResult.data &&
      RcrmAppAvailableResult.data.foundApp == false
    ) {
      changeCurrentTab("g-sheets");
    }
  }, []);

  const getFacebookSettings = async () => {
    await dispatch(
      facebookActions.getFacebookSettings({
        token: rmpToken,
      }),
    );

    await dispatch(
      facebookActions.getFacebookCRMConversionLastSent({
        token: rmpToken,
      }),
    );
    if (menu === sessionStorage.getItem("selectedMenu")) {
      setLoading(false);
    }
  };

  const handleActiveConversionCloseModal = () => {
    setModalActiveConversionDisplay("hide");
  };

  const handleInactiveConversionCloseModal = () => {
    setModalInactiveConversionDisplay("hide");
  };

  const handleClickActiveConversion = (event) => {
    event.preventDefault();

    if (facebookSettingsResult.data[0].crm_conversion_status === "active") {
      setModalInactiveConversionDisplay("show");
    } else {
      setModalActiveConversionDisplay("show");
    }
  };

  const handleActiveConversion = async () => {
    setConversionStatusLoading(true);
    const response = await dispatch(
      facebookActions.setFacebookConversionStatus({
        token: rmpToken,
        fbConversionStatus: "on",
        conversionType: "crm",
      }),
    );

    if (response.result === 0) {
      setShowSuccessToast(true);
    } else {
      setShowFailedToast(true);
    }

    facebookSettingsResult.data[0].crm_conversion_status = "active";

    setModalActiveConversionDisplay("hide");
    setConversionStatusLoading(false);
  };

  const handleInactiveConversion = async () => {
    setConversionStatusLoading(true);
    const response = await dispatch(
      facebookActions.setFacebookConversionStatus({
        token: rmpToken,
        fbConversionStatus: "off",
        conversionType: "crm",
      }),
    );

    if (response.result === 0) {
      setShowSuccessToast(true);
    } else {
      setShowFailedToast(true);
    }

    facebookSettingsResult.data[0].crm_conversion_status = "inactive";

    setModalInactiveConversionDisplay("hide");
    setConversionStatusLoading(false);
  };

  const handleChangeActiveLeadConversion = () => {
    console.log("handleChangeActiveLeadConversion");
    // Do nothing
  };

  const successToastCallback = (value) => {
    setShowSuccessToast(value);
  };

  const failedToastCallback = (value) => {
    setShowFailedToast(value);
  };

  const modalActiveConversionContent = (
    <ModalContentContainer>
      <div className="info-icon">&#9432;</div>
      <div>{t("__fbads_crm_conversion_active_confirm")}</div>
    </ModalContentContainer>
  );

  const modalActiveConversionFooter = (
    <ModalButtonContainer>
      <Button type="secondary" onClick={handleActiveConversionCloseModal}>
        {t("__cancel")}
      </Button>
      <Button type="loading" onClick={handleActiveConversion} loading={conversionStatusLoading}>
        {t("__confirm")}
      </Button>
    </ModalButtonContainer>
  );
  const modalInactiveConversionContent = (
    <ModalContentContainer>
      <div className="info-icon">&#9432;</div>
      <div>{t("__fbads_crm_conversion_inactive_confirm")}</div>
    </ModalContentContainer>
  );
  const modalInactiveConversionFooter = (
    <ModalButtonContainer>
      <Button type="secondary" onClick={handleInactiveConversionCloseModal}>
        {t("__cancel")}
      </Button>
      <Button type="loading" onClick={handleInactiveConversion} loading={conversionStatusLoading}>
        {t("__confirm")}
      </Button>
    </ModalButtonContainer>
  );

  return (
    <>
      <RelativeDiv>
        <SetupTextHeader>{t("__fbads_crm_conversion_title")}</SetupTextHeader>
        <TextHeaderDescription>{t("__fbads_crm_conversion_description")}</TextHeaderDescription>
        {conversionLoading.data.loading ? (
          <Loading />
        ) : (
          <>
            <ConversionSettingsContainer>
              <FlexSpaceBetween>
                <ConversionStatusLabel>{t("__fbads_conversion_sync")}</ConversionStatusLabel>
                <div>
                  <ToggleSwitch
                    value="on"
                    checked={
                      facebookSettingsResult.data[0].crm_conversion_status === "active" &&
                      detailsPixelId.result === 0 &&
                      detailsPixelId.msg === "GET_DATA_COMPLETED" &&
                      detailsPixelId.data.is_crm
                    }
                    onClick={(event) => handleClickActiveConversion(event)}
                    onChange={handleChangeActiveLeadConversion}
                    disabled={
                      !(
                        detailsPixelId.result === 0 &&
                        detailsPixelId.msg === "GET_DATA_COMPLETED" &&
                        detailsPixelId.data.is_crm
                      )
                    }
                  ></ToggleSwitch>
                </div>
              </FlexSpaceBetween>
              <FlexCenter>
                <div>
                  <ConnectionImage
                    src={
                      facebookSettingsResult.data[0].crm_conversion_status === "active" &&
                      detailsPixelId.result === 0 &&
                      detailsPixelId.msg === "GET_DATA_COMPLETED" &&
                      detailsPixelId.data.is_crm
                        ? "/images/meta_connected.png"
                        : "/images/meta_disconnected.png"
                    }
                  />
                </div>
              </FlexCenter>
            </ConversionSettingsContainer>

            {facebookSettingsResult.data[0].crm_conversion_status === "active" &&
            detailsPixelId.result === 0 &&
            detailsPixelId.msg === "GET_DATA_COMPLETED" &&
            detailsPixelId.data.is_crm ? (
              loading === true ? (
                <Loading />
              ) : (
                <div>
                  <Tabs>
                    {RcrmAppAvailableResult &&
                    RcrmAppAvailableResult.result === 0 &&
                    RcrmAppAvailableResult.data &&
                    RcrmAppAvailableResult.data.foundApp ? (
                      <TabPanel
                        className={currentTab === "r-crm" ? "selected" : ""}
                        onClick={() => {
                          changeCurrentTab("r-crm");
                        }}
                      >
                        R-CRM
                      </TabPanel>
                    ) : (
                      ""
                    )}
                    {(REACT_APP_ENABLE_GOOGLE == "TRUE" ||
                      (businessInfoResult?.data?.business_id &&
                        REACT_APP_ENABLE_GOOGLE_WHITELIST.search(businessInfoResult?.data?.business_id) >= 0)) && (
                      <TabPanel
                        className={currentTab === "g-sheets" ? "selected" : ""}
                        onClick={() => {
                          changeCurrentTab("g-sheets");
                        }}
                      >
                        Google Sheets
                      </TabPanel>
                    )}
                    <TabDummy></TabDummy>
                  </Tabs>
                  <div>{RenderContent}</div>
                </div>
              )
            ) : facebookSettingsResult.data[0].crm_conversion_status === "inactive" &&
              detailsPixelId.result === 0 &&
              detailsPixelId.msg === "GET_DATA_COMPLETED" &&
              detailsPixelId.data.is_crm ? null : (
              <WarningArea>
                <>
                  <div className="warning-text">{t("__fbads_crm_conversion_connection_crm_warning")}</div>
                  <StyledLink target="_blank" rel="noopener noreferrer" href={REACT_APP_GOOGLE_ADS_SETUP_MANUAL_URL}>
                    <HelpIcon src="/images/help_outline.svg" />
                    {t("__ads_setup_manual_link")}
                  </StyledLink>
                </>
              </WarningArea>
            )}

            <Toast
              show={showSuccessToast}
              message={t("__ads_offline_conversion_save_success")}
              bgColor="#4CAF50"
              callback={successToastCallback}
            ></Toast>
            <Toast
              show={showFailedToast}
              message={t("__ads_offline_conversion_save_failed")}
              bgColor="#F44336"
              callback={failedToastCallback}
            ></Toast>
            <Modal
              state={modalActiveConversionDisplay}
              content={modalActiveConversionContent}
              footer={modalActiveConversionFooter}
            />
            <Modal
              state={modalInactiveConversionDisplay}
              content={modalInactiveConversionContent}
              footer={modalInactiveConversionFooter}
            />
          </>
        )}
      </RelativeDiv>
    </>
  );
};

export default CRMConversion;

const RelativeDiv = styled.div`
  position: relative;
`;

const SetupTextHeader = styled(TextHeader)`
  font-size: 18px;
  margin-bottom: 10px;
  font-weight: bold;
`;

const TextHeaderDescription = styled.div`
  font-size: 14px;
  color: #999;
  margin-bottom: 10px;
`;

const ConversionSettingsContainer = styled.div`
  border-radius: 8px;
  border: 1px solid #eee;
  padding: 20px;
`;

const FlexSpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FlexCenter = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ConnectionImage = styled.img`
  margin-top: 20px;
  width: 100%;
  max-width: 300px;
`;

const ConversionStatusLabel = styled.div`
  color: #999;
  font-size: 0.9rem;
`;

const Tabs = styled.div`
  margin-top: 10px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding-left: 0px;
  border-bottom: 1px solid #eee;

  @media (max-width: 768px) {
    width: 90%;
  }
`;

const TabPanel = styled.div`
  min-width: 100px;
  max-width: 50%;
  padding: 10px;
  list-style: none;
  text-align: center;
  cursor: pointer;

  &.selected {
    border-bottom: 2px solid #0088e3;
  }
`;

const TabDummy = styled.div`
  flex-grow: 1;
`;

const ModalContentContainer = styled.div`
  text-align: center;
  margin-bottom: 20px;
  color: #575757;
  font-weight: bold;

  .info-icon {
    font-size: 78px;
    color: #f8bb86;
    margin-bottom: 20px;
    font-weight: normal;
  }
`;

const ModalButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 16px;

  > Button {
    min-width: 100px;
    margin-bottom: 0px;
  }

  > Button:first-child {
    margin-right: 10px;
  }
`;

const WarningArea = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 20px;
  font-size: 0.9rem;
  .warning-text {
    color: ${colors.negative};
    margin-right: 10px;
  }
`;

const StyledLink = styled.a`
  text-decoration: none;
  color: #337ab7;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
  width: fit-content;

  &:hover {
    text-decoration: underline;
  }
`;

const HelpIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 6px;
`;
